// License List Page — All licenses with filtering, search, bulk actions
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { useToast } from '@/components/shared/ToastProvider';
import { MaskedKey, maskKeyValue } from '@/components/shared/MaskedKey';
import { licensesToCsv } from '@/lib/license-export';
import { Search, Plus, Filter, Download, RefreshCw, ChevronLeft, ChevronRight, X } from 'lucide-react';

interface License {
  id: string;
  license_key: string;
  organization_id: string;
  organization_name: string;
  product_name: string;
  tier: string;
  status: string;
  // Capacity only. GET /licenses reports no seat usage, so the list has no "used" figure to show
  // (it used to hard-code 0, which read as "no seats in use" and was exported as such).
  seats_total: number;
  expires_at: string | null;
  created_at: string;
  hardware_id: string | null;
  // 18H §4.1 — license-class badge/filter (install/platform/saas), orthogonal
  // to `scope` (25_trust_saas_foundation.sql license_class).
  license_class: 'install' | 'platform' | 'saas';
}

interface LicenseListResponse {
  licenses: License[];
  total: number;
  page: number;
  pageSize: number;
}

type BulkAction = 'suspend' | 'revoke' | 'reactivate';

// POST /licenses/bulk answers per row (backend license.routes.ts BulkLicenseResult).
interface BulkResponse {
  action: BulkAction;
  requested: number;
  succeeded: number;
  failed: number;
  results: Array<{ id: string; ok: boolean; reason?: string }>;
}

const BULK_PAST: Record<BulkAction, string> = { suspend: 'suspended', revoke: 'revoked', reactivate: 'reactivated' };

/** What the operator is told once a bulk call has answered: counts, and the rows that were not changed. */
interface BulkOutcome {
  action: BulkAction;
  succeeded: number;
  notChanged: Array<{ id: string; organization: string; key: string; reason: string }>;
}

/** The licences a bulk confirm is about, named so the operator signs off on rows, not on a count. */
function AffectedLicenses({ rows }: { rows: Array<Pick<License, 'id' | 'organization_name' | 'license_key'>> }) {
  return (
    <ul
      aria-label="Licenses affected"
      style={{ margin: 0, padding: '8px 12px', listStyle: 'none', maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-secondary)', fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {rows.map((r) => (
        <li key={r.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.organization_name}</span>
          <span style={{ fontFamily: 'var(--mono, monospace)', color: 'var(--text-muted)' }}>{maskKeyValue(r.license_key)}</span>
        </li>
      ))}
    </ul>
  );
}

export function LicenseListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Pending bulk action awaiting confirmation (null = dialog closed).
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  // The rows the pending action covers, fixed when it is requested: the dialog names them, the call
  // is checked against them, and a dialog that is still closing (the selection is cleared when the
  // call succeeds) must not read "Suspend 0 licenses?".
  const [pendingRows, setPendingRows] = useState<License[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const statusFilter = searchParams.get('status') || '';
  const tierFilter = searchParams.get('tier') || '';
  const classFilter = searchParams.get('class') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 20;

  // One key for "what the table is showing": any change of page, filter, search or status is a
  // different set of rows, and the old ticks must not follow the operator to it (PR #94 review).
  const viewKey = [statusFilter, tierFilter, classFilter, search, page].join('\u0000');
  useEffect(() => {
    setSelectedIds(new Set());
    setPendingAction(null);
  }, [viewKey]);

  const loadLicenses = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (tierFilter) params.set('tier', tierFilter);
      if (classFilter) params.set('class', classFilter);
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      // Backend shape: { data: License[], pagination: { total } } with org joined.
      const resp = await apiRequest<any>(`/licenses?${params}`);
      const rows: License[] = (resp.data || []).map((l: any) => ({
        id: l.id,
        license_key: l.license_id,
        organization_id: l.organization_id,
        organization_name: l.organization?.name || l.org_name || '—',
        product_name: l.scope === 'platform' ? 'Platform' : 'Org',
        tier: l.tier,
        status: l.status,
        seats_total: l.scope === 'platform' ? (l.included_org_seats || 0) : (l.vendor_limit || 0),
        expires_at: l.expires_at,
        created_at: l.created_at,
        hardware_id: l.scope || null,
        license_class: l.license_class || 'install',
      }));
      setLicenses(rows);
      setTotal(resp.pagination?.total ?? rows.length);
      // A re-read can drop a row. A ticked row that is no longer listed is no longer selected, and
      // must not come back ticked if a later read lists it again.
      const listed = new Set(rows.map((r) => r.id));
      setSelectedIds((prev) => (prev.size === 0 ? prev : new Set(Array.from(prev).filter((id) => listed.has(id)))));
    } catch (err) {
      setLicenses([]);
      setSelectedIds(new Set());
      setTotal(0);
      setLoadError(err instanceof Error ? err.message : 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter, classFilter, search, page]);

  useEffect(() => { loadLicenses(); }, [loadLicenses]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // What the operator can SEE (not the loading placeholder, not a failed read) and has ticked. The
  // bar, the header checkbox and every bulk call work from these two lists and never from the raw
  // Set, which may hold an id the table no longer shows.
  const visibleRows = loading || loadError ? [] : licenses;
  const visibleSelected = visibleRows.filter((l) => selectedIds.has(l.id));
  const allVisibleSelected = visibleRows.length > 0 && visibleSelected.length === visibleRows.length;

  const toggleAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(visibleRows.map((l) => l.id)));
  };

  const requestBulkAction = (action: BulkAction) => {
    if (visibleSelected.length === 0) return;
    setPendingRows(visibleSelected);
    setPendingAction(action);
  };

  // POST /licenses/bulk. Throws the server's message when the CALL fails (the dialog stays open and
  // shows it, the selection is kept); a row the server could not change is a row in the outcome.
  const runBulkAction = async (action: BulkAction, reason?: string) => {
    // Only the rows the dialog named, and only while they are still on screen. If the list was
    // re-read under the open dialog and a named row is gone, nothing is sent: a count or a typed
    // "REVOKE 3" confirmed three rows, not two.
    const onScreen = new Set(visibleRows.map((l) => l.id));
    const targets = pendingRows.filter((l) => onScreen.has(l.id));
    if (targets.length === 0 || targets.length !== pendingRows.length) {
      throw new Error(
        targets.length === 0
          ? 'None of the selected licenses is on screen any more. Nothing was changed; close this and select again.'
          : `${pendingRows.length - targets.length} of the ${pendingRows.length} selected licenses left the page while this was open. Nothing was changed; close this and select again.`,
      );
    }
    const ids = targets.map((l) => l.id);
    const rowOf = new Map(targets.map((l) => [l.id, l]));
    setBulkBusy(true);
    try {
      const resp = await apiRequest<BulkResponse>('/licenses/bulk', {
        method: 'POST',
        body: JSON.stringify({ ids, action, ...(reason ? { reason } : {}) }),
      });
      const notChanged = (resp.results || [])
        .filter((r) => !r.ok)
        .map((r) => ({
          id: r.id,
          organization: rowOf.get(r.id)?.organization_name ?? '—',
          key: rowOf.get(r.id)?.license_key ?? r.id,
          reason: r.reason || 'Not changed',
        }));
      setBulkOutcome({ action, succeeded: resp.succeeded ?? ids.length - notChanged.length, notChanged });
      setSelectedIds(new Set());
      loadLicenses();
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCurrentPage = () => {
    try {
      const blob = new Blob([licensesToCsv(licenses)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `licenses-page-${page}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        kind: 'error',
        title: 'License export failed',
        body: err instanceof Error ? err.message : 'Unable to create the CSV file.',
      });
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Licenses</h1>
          {/* A total exists only for a list that loaded (a real zero included). While loading, or
              after a failed load, total is 0 and "0 licenses total" would be a figure the page
              does not have. */}
          {!loading && !loadError && <p className="page-subtitle">{total} licenses total</p>}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={loadLicenses}
            style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={exportCurrentPage}
            disabled={loading || licenses.length === 0}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: loading || licenses.length === 0 ? 'not-allowed' : 'pointer', opacity: loading || licenses.length === 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}
          >
            <Download size={14} /> Export current page
          </button>
          <Link to="/licenses/new">
            <button style={{ background: 'var(--accent)', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Plus size={14} /> Issue License
            </button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search licenses or organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none' }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { const p = new URLSearchParams(searchParams); p.set('status', e.target.value); p.set('page', '1'); setSearchParams(p); }}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', cursor: 'pointer' }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="suspended">Suspended</option>
          <option value="revoked">Revoked</option>
          <option value="trial">Trial</option>
        </select>
        <select
          value={tierFilter}
          onChange={(e) => { const p = new URLSearchParams(searchParams); p.set('tier', e.target.value); p.set('page', '1'); setSearchParams(p); }}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', cursor: 'pointer' }}
        >
          <option value="">All Tiers</option>
          <option value="trial">Trial</option>
          <option value="starter">Starter</option>
          <option value="professional">Professional</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select
          value={classFilter}
          onChange={(e) => { const p = new URLSearchParams(searchParams); p.set('class', e.target.value); p.set('page', '1'); setSearchParams(p); }}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', cursor: 'pointer' }}
        >
          <option value="">All Classes</option>
          <option value="install">Install</option>
          <option value="platform">Platform</option>
          <option value="saas">SaaS</option>
        </select>
      </div>

      {/* The last bulk call, per row: what changed, and which rows were not changed and why. */}
      {bulkOutcome && (
        <div role="status" style={{ padding: '10px 16px', background: bulkOutcome.notChanged.length ? 'var(--ps-pill-amber-bg)' : 'var(--ps-pill-green-bg)', color: bulkOutcome.notChanged.length ? 'var(--ps-pill-amber-tx)' : 'var(--ps-pill-green-tx)', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <strong>{bulkOutcome.succeeded} {BULK_PAST[bulkOutcome.action]}, {bulkOutcome.notChanged.length} not changed</strong>
            <button onClick={() => setBulkOutcome(null)} aria-label="Dismiss bulk result" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', border: '1px solid currentColor', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '12px' }}>
              <X size={12} aria-hidden="true" /> Dismiss
            </button>
          </div>
          {bulkOutcome.notChanged.length > 0 && (
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {bulkOutcome.notChanged.map((r) => (
                <li key={r.id} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{r.organization}</span>
                  <MaskedKey value={r.key} size={12} />
                  <span>— {r.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Bulk actions */}
      {visibleSelected.length > 0 && (
        <div style={{ padding: '10px 16px', background: 'var(--accent-light)', borderRadius: '8px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}>
          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{visibleSelected.length} selected</span>
          <button onClick={() => requestBulkAction('suspend')} style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '12px' }}>Suspend</button>
          <button onClick={() => requestBulkAction('revoke')} style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', cursor: 'pointer', fontSize: '12px' }}>Revoke</button>
          <button onClick={() => requestBulkAction('reactivate')} style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--ps-pill-green-bg)', background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)', cursor: 'pointer', fontSize: '12px' }}>Reactivate</button>
          <button onClick={() => setSelectedIds(new Set())} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '12px' }}>Clear</button>
        </div>
      )}

      {/* Table */}
      {loadError && <div role="alert" style={{ marginBottom: 12, color: 'var(--ps-pill-red-tx)' }}>
        {loadError} <button onClick={loadLicenses}>Retry</button>
      </div>}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', width: '32px' }}>
                  <input type="checkbox" aria-label="Select all licenses on this page" checked={allVisibleSelected} onChange={toggleAll} />
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>License Key</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Organization</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Class</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Tier</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Seats</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Expires</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                    Loading licenses...
                  </td>
                </tr>
              ) : loadError ? (
                <tr><td colSpan={9} style={{ padding: 24 }}>License data is unavailable.</td></tr>
              ) : licenses.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 0 }}>
                    <EmptyState
                      icon="key"
                      title="No licenses found"
                      body={search || statusFilter || tierFilter || classFilter ? 'No licenses match your filters. Try clearing them.' : 'No licenses have been issued yet.'}
                    />
                  </td>
                </tr>
              ) : (
                licenses.map((license) => (
                  <tr
                    key={license.id}
                    style={{ borderBottom: '1px solid var(--border)', background: selectedIds.has(license.id) ? 'var(--accent-light)' : 'transparent', transition: 'background 0.1s' }}
                    onMouseEnter={(e) => { if (!selectedIds.has(license.id)) (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = selectedIds.has(license.id) ? 'var(--accent-light)' : 'transparent'; }}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <input type="checkbox" aria-label={`Select ${license.organization_name}`} checked={selectedIds.has(license.id)} onChange={() => toggleSelect(license.id)} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Link to={`/licenses/${license.id}`} style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>
                        <MaskedKey value={license.license_key} size={12} />
                      </Link>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '13px' }}>
                      <Link to={`/organizations/${license.organization_id}`} style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {license.organization_name}
                      </Link>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusBadge status={license.license_class} />
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                      {license.license_class === 'saas' ? '—' : license.tier}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusBadge status={license.status} />
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-secondary)' }} title="Seats this licence allows. Seats in use are not reported by the license list.">
                      {license.seats_total}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: license.expires_at && new Date(license.expires_at) < new Date() ? 'var(--status-expired)' : 'var(--text-secondary)' }}>
                      {license.expires_at ? new Date(license.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {formatRelativeTime(license.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span>Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                disabled={page <= 1}
                onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page - 1)); setSearchParams(p); }}
                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(pageNum)); setSearchParams(p); }}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: page === pageNum ? 'var(--accent)' : 'var(--surface)', color: page === pageNum ? 'white' : 'inherit', cursor: 'pointer' }}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages}
                onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page + 1)); setSearchParams(p); }}
                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suspend and Revoke are audit-logged and the server requires a reason (1-500); Revoke is
          irreversible, so it also needs the typed phrase "REVOKE n" (36E NM-L21). */}
      <ReasonDialog
        open={pendingAction === 'suspend'}
        title={`Suspend ${pendingRows.length} ${pendingRows.length === 1 ? 'license' : 'licenses'}?`}
        body="Suspended licenses stop validating until they are reactivated. The reason is logged to the audit trail for every row."
        details={<AffectedLicenses rows={pendingRows} />}
        confirmLabel={`Suspend ${pendingRows.length}`}
        maxLength={500}
        onConfirm={async (reason) => { await runBulkAction('suspend', reason); setPendingAction(null); }}
        onCancel={() => setPendingAction(null)}
      />
      <ReasonDialog
        open={pendingAction === 'revoke'}
        title={`Revoke ${pendingRows.length} ${pendingRows.length === 1 ? 'license' : 'licenses'}?`}
        body="Revoked licenses stop validating on installs using these keys and their seats free back to the pool. This cannot be undone. The reason is logged to the audit trail for every row."
        details={<AffectedLicenses rows={pendingRows} />}
        confirmLabel={`Revoke ${pendingRows.length}`}
        danger
        maxLength={500}
        confirmPhrase={`REVOKE ${pendingRows.length}`}
        onConfirm={async (reason) => { await runBulkAction('revoke', reason); setPendingAction(null); }}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDialog
        open={pendingAction === 'reactivate'}
        title={`Reactivate ${pendingRows.length} ${pendingRows.length === 1 ? 'license' : 'licenses'}?`}
        body="The selected licenses resume validating. Licenses that are not suspended are left as they are and listed in the result."
        details={<AffectedLicenses rows={pendingRows} />}
        confirmLabel="Reactivate"
        busy={bulkBusy}
        onConfirm={async () => {
          try {
            await runBulkAction('reactivate');
          } catch (err) {
            toast({ kind: 'error', title: 'Bulk reactivate failed', body: err instanceof Error ? err.message : 'Unknown error' });
          } finally {
            setPendingAction(null);
          }
        }}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
